import React, { FunctionComponent, PropsWithChildren, useEffect } from 'react';
import { getNavbarCategories } from '../services/configurator/navbar/NavbarConfigurator';
import { searchArticles } from '../services/content/article/Article';
import { NavigationMenuContext } from '../contexts/NavigationMenuContext';
import Layout from '../components/Layout/Layout';
import Head from 'next/head';
import { SectionSecondaryContent } from '../components/Section/SectionSecondaryContent';
import styles from '../styles/Tags.module.scss';
import { ArticleModel } from '../services/content/article/models/ArticleModel';
import { CategoryModel } from '../services/content/category/models/CategoryModel';
import { TagModel } from '../services/content/tag/models/TagModel';
import { setupSearchTargeting } from '../services/ads/Targeting';
import { Pagination } from '../components/Partials/Pagination/Pagination';
import { MetaDataModel } from '../services/content/meta/models/MetaDataModel';
import { useRouter } from 'next/router';
import { AdBackground } from '../components/Ads/AdBackground';
import { GoogleAdDesktop } from '../components/Ads/GoogleAdDesktop';
import { ads } from '../services/ads/constants';
import { GoogleAdMobile } from '../components/Ads/GoogleAdMobile';
import { Routes } from '../constants/Routes';
import { LdJson } from '../components/LdJson/LdJson';
import { Pages } from '../constants/Enums';
import { authorCache, categoryCache } from '../services/Cache';

type Props = {
    articles: ArticleModel[];
    categories: CategoryModel[];
    tag: TagModel | null;
    meta: MetaDataModel;
    page: string;
    searchText: string;
    q: any;
};

export async function getServerSideProps(context: any) {
    context.res.setHeader('Cache-Control', 'public, s-maxage=14, stale-while-revalidate=59');
    const searchQuery = context.query.q;
    const page = context.query.page ? context.query.page : '1';

    const cacheKey = `/search?q=${searchQuery}&page=${page}`;

    if (authorCache.get(cacheKey)) {
        const props = categoryCache.get(cacheKey);
        if (props) {
            return {
                articles: props.articles,
                meta: props.meta,
                categories: props.categories,
                page: page,
                searchText: searchQuery,
            };
        }
    }

    const categories = await getNavbarCategories();
    const articleListing = await searchArticles(searchQuery, '18', page);
    const articles = articleListing && articleListing.articles ? articleListing.articles : [];
    const meta = articleListing && articleListing.meta ? articleListing.meta : null;

    authorCache.set(cacheKey, {
        articles: articles,
        meta: meta,
        categories: categories,
        page: page,
        searchText: searchQuery,
    });

    return {
        props: {
            articles: articles,
            meta: meta,
            categories: categories,
            page: page,
            searchText: searchQuery,
        },
    };
}

const SearchTextPage: FunctionComponent<Props> = ({ categories, articles, tag, meta, page, searchText }) => {
    const router = useRouter();

    useEffect(() => {
        setupSearchTargeting();
    }, []);

    return (
        <NavigationMenuContext.Provider value={categories}>
            <Layout>
                <Head>
                    <LdJson page={Pages.SEARCH} />
                    <title>Общи резултати от търсенето за {searchText}</title>
                    <script
                        dangerouslySetInnerHTML={{
                            __html: `
                          window['sprtlTargeting'] = {
                                section: ['search']
                          }`,
                        }}></script>
                    <link rel='canonical' href={`${process.env.NEXT_PUBLIC_WEBSITE_URL}`} />
                </Head>
                <main className='container'>
                    <div className='row mb-5'>
                        <div className={`${styles.description} col-12 font-lato-200`}>{tag?.description}</div>
                    </div>
                    <div className='d-flex flex-row'>
                        <div className='container'>
                            <div className='row'>
                                {articles.map((article, idx) => {
                                    return (
                                        <AdsMobileWrapper
                                            idx={idx}
                                            key={`section-secondary-ads-mobile-key-${article.id}`}>
                                            <SectionSecondaryContent
                                                className={`${styles.secondaryContentContainer} col-md-4 col-lg-4 col-12 mb-md-3`}
                                                content={article}
                                                category={article?.category}
                                            />
                                        </AdsMobileWrapper>
                                    );
                                })}
                            </div>
                            <div className='w-100 text-center'>
                                <div className='d-inline-block d-md-none d-lg-none text-center'>
                                    <AdBackground>
                                        <GoogleAdMobile ad={ads[13]} />
                                    </AdBackground>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-12 d-flex flex-row justify-content-end'>
                                    <Pagination
                                        meta={meta}
                                        onPageClick={(page) => {
                                            router.push(
                                                page > 1
                                                    ? Routes.openSearch(searchText, `${page}`)
                                                    : Routes.openSearch(searchText),
                                            );
                                        }}
                                        page={page}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='d-none d-md-block col-12 col-md-3 text-center'>
                            <AdBackground className='sticky-ad'>
                                <GoogleAdDesktop ad={ads[10]} />
                            </AdBackground>
                        </div>
                    </div>
                </main>
            </Layout>
        </NavigationMenuContext.Provider>
    );
};

export default SearchTextPage;

const AdsMobileWrapper: FunctionComponent<PropsWithChildren<{ idx: number }>> = ({ children, idx }) => {
    const SECOND_ARTICLE = 1;
    const FOURTH_ARTICLE = 3;
    const SEVENTH_ARTICLE = 6;

    if (SECOND_ARTICLE === idx) {
        return (
            <>
                {children}
                <div className='d-inline-block d-md-none d-lg-none text-center'>
                    <AdBackground className='sticky-ad'>
                        <GoogleAdMobile ad={ads[10]} />
                    </AdBackground>
                </div>
            </>
        );
    }

    if (FOURTH_ARTICLE === idx) {
        return (
            <>
                {children}
                <div className='d-inline-block d-md-none d-lg-none text-center ad-container-h600'>
                    <AdBackground className='sticky-ad'>
                        <GoogleAdMobile ad={ads[11]} />
                    </AdBackground>
                </div>
            </>
        );
    }

    if (SEVENTH_ARTICLE === idx) {
        return (
            <>
                {children}
                <div className='d-inline-block d-md-none d-lg-none text-center'>
                    <AdBackground className='sticky-ad'>
                        <GoogleAdMobile ad={ads[12]} />
                    </AdBackground>
                </div>
            </>
        );
    }

    return <>{children}</>;
};
